import React, { useState } from 'react';
import { useFilters } from '../../contexts/FiltersContext';
import { InvestmentAssumptions } from '../../types/filters';

const Tip: React.FC<{ text: string }> = ({ text }) => (
  <div className="relative group inline-block ml-1">
    <span className="text-blue-400 cursor-help text-xs leading-none select-none">ⓘ</span>
    <div className="pointer-events-none absolute top-full left-0 mt-1 hidden group-hover:block z-[9999] w-72 bg-gray-900 text-white text-xs rounded-lg p-2.5 shadow-xl whitespace-normal leading-relaxed">
      {text}
      <div className="absolute bottom-full left-3 border-4 border-transparent border-b-gray-900" />
    </div>
  </div>
);

type Mode = 'pct' | 'fixed';

const Toggle: React.FC<{ mode: Mode; onToggle: (m: Mode) => void }> = ({ mode, onToggle }) => (
  <div className="flex border border-gray-300 rounded overflow-hidden text-[10px] w-fit mb-0.5 self-start">
    <button
      onClick={() => onToggle('pct')}
      className={`px-1.5 py-0.5 leading-none ${mode === 'pct' ? 'bg-blue-500 text-white' : 'bg-white text-gray-400 hover:bg-gray-100'}`}
    >%</button>
    <button
      onClick={() => onToggle('fixed')}
      className={`px-1.5 py-0.5 leading-none ${mode === 'fixed' ? 'bg-blue-500 text-white' : 'bg-white text-gray-400 hover:bg-gray-100'}`}
    >$</button>
  </div>
);

const InputWrap: React.FC<React.InputHTMLAttributes<HTMLInputElement> & { suffix: string }> = ({ suffix, className, ...props }) => (
  <div className="relative w-full">
    <input {...props} className={`${className} pr-7`} />
    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none select-none">{suffix}</span>
  </div>
);

const FIXED_KEYS: Record<string, keyof InvestmentAssumptions> = {
  down_payment: 'down_payment_fixed',
  insurance: 'insurance_fixed',
  maintenance: 'maintenance_fixed',
  vacancy: 'vacancy_fixed',
  property_mgmt: 'property_mgmt_fixed',
  closing: 'closing_costs_fixed',
  escrow: 'escrow_fixed',
  property_tax: 'property_tax_fixed',
};

const AssumptionsBar: React.FC = () => {
  const { assumptions, updateAssumption, resetAssumptions } = useFilters();

  const [modes, setModes] = useState<Record<string, Mode>>({
    down_payment:  assumptions.down_payment_fixed  != null ? 'fixed' : 'pct',
    insurance:     assumptions.insurance_fixed     != null ? 'fixed' : 'pct',
    maintenance:   assumptions.maintenance_fixed   != null ? 'fixed' : 'pct',
    vacancy:       assumptions.vacancy_fixed       != null ? 'fixed' : 'pct',
    property_mgmt: assumptions.property_mgmt_fixed != null ? 'fixed' : 'pct',
    closing:       assumptions.closing_costs_fixed != null ? 'fixed' : 'pct',
    escrow:        assumptions.escrow_fixed        != null ? 'fixed' : 'pct',
    property_tax:  assumptions.property_tax_fixed  != null ? 'fixed' : 'pct',
  });

  // Local state for all numeric inputs - prevents losing focus on keystroke
  const [downPaymentPctInput, setDownPaymentPctInput] = useState<string>(assumptions.down_payment_pct !== undefined ? Math.round(assumptions.down_payment_pct * 100).toString() : '');
  const [downPaymentFixedInput, setDownPaymentFixedInput] = useState<string>(assumptions.down_payment_fixed?.toString() || '');
  const [pmiRateInput, setPmiRateInput] = useState<string>(assumptions.pmi_rate !== undefined ? (assumptions.pmi_rate * 100).toFixed(2) : '');
  const [interestRateInput, setInterestRateInput] = useState<string>(assumptions.interest_rate !== undefined ? (assumptions.interest_rate * 100).toFixed(2) : '');
  const [insurancePctInput, setInsurancePctInput] = useState<string>(assumptions.insurance_pct !== undefined ? (assumptions.insurance_pct * 100).toFixed(2) : '');
  const [insuranceFixedInput, setInsuranceFixedInput] = useState<string>(assumptions.insurance_fixed?.toString() || '');
  const [maintenancePctInput, setMaintenancePctInput] = useState<string>(assumptions.maintenance_pct !== undefined ? (assumptions.maintenance_pct * 100).toFixed(2) : '');
  const [maintenanceFixedInput, setMaintenanceFixedInput] = useState<string>(assumptions.maintenance_fixed?.toString() || '');
  const [vacancyRateInput, setVacancyRateInput] = useState<string>(assumptions.vacancy_rate !== undefined ? Math.round(assumptions.vacancy_rate * 100).toString() : '');
  const [vacancyFixedInput, setVacancyFixedInput] = useState<string>(assumptions.vacancy_fixed?.toString() || '');
  const [propertyMgmtPctInput, setPropertyMgmtPctInput] = useState<string>(assumptions.property_mgmt_pct !== undefined ? Math.round(assumptions.property_mgmt_pct * 100).toString() : '');
  const [propertyMgmtFixedInput, setPropertyMgmtFixedInput] = useState<string>(assumptions.property_mgmt_fixed?.toString() || '');
  const [closingCostsPctInput, setClosingCostsPctInput] = useState<string>(assumptions.closing_costs_pct !== undefined ? (assumptions.closing_costs_pct * 100).toFixed(1) : '');
  const [closingCostsFixedInput, setClosingCostsFixedInput] = useState<string>(assumptions.closing_costs_fixed?.toString() || '');
  const [escrowPctInput, setEscrowPctInput] = useState<string>(assumptions.escrow_pct !== undefined ? (assumptions.escrow_pct * 100).toFixed(1) : '');
  const [escrowFixedInput, setEscrowFixedInput] = useState<string>(assumptions.escrow_fixed?.toString() || '');
  const [propertyTaxPctInput, setPropertyTaxPctInput] = useState<string>(assumptions.property_tax_rate !== undefined ? (assumptions.property_tax_rate * 100).toFixed(2) : '');
  const [propertyTaxFixedInput, setPropertyTaxFixedInput] = useState<string>(assumptions.property_tax_fixed?.toString() || '');
  const [reservesInput, setReservesInput] = useState<string>(assumptions.reserves_amount?.toString() || '');

  // Immediate commit helpers - update context instantly for live recalc
  const commitNumber = (key: keyof InvestmentAssumptions, value: string, defaultValue?: number) => {
    const trimmed = value.trim();
    updateAssumption(key, trimmed ? Number(trimmed) : defaultValue);
  };

  const commitPercent = (key: keyof InvestmentAssumptions, value: string, defaultValue?: number) => {
    const trimmed = value.trim();
    updateAssumption(key, trimmed ? Number(trimmed) / 100 : defaultValue);
  };

  // Update both local state AND context on every keystroke
  const handleNumberChange = (
    localSetter: React.Dispatch<React.SetStateAction<string>>,
    contextKey: keyof InvestmentAssumptions,
    value: string,
    defaultValue?: number
  ) => {
    localSetter(value);
    commitNumber(contextKey, value, defaultValue);
  };

  const handlePercentChange = (
    localSetter: React.Dispatch<React.SetStateAction<string>>,
    contextKey: keyof InvestmentAssumptions,
    value: string,
    defaultValue?: number
  ) => {
    localSetter(value);
    commitPercent(contextKey, value, defaultValue);
  };

  const tog = (field: string, m: Mode) => {
    setModes(prev => ({ ...prev, [field]: m }));
    if (m === 'pct') updateAssumption(FIXED_KEYS[field], undefined);
  };

  const inputCls = "w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-400 focus:border-blue-400 bg-white";

  return (
    <div className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
      <div className="flex items-end gap-3 px-4 py-2 flex-wrap">
        <div className="flex items-end gap-3 flex-1 justify-between">

          {/* 1. Loan Term — no toggle */}
          <div className="flex flex-col">
            <label className="flex items-center text-xs text-gray-500 mb-0.5 whitespace-nowrap">
              Loan Term<Tip text="Mortgage length. 30yr = lower payment, more interest. 15yr = higher payment, faster equity. Default: 30yr." />
            </label>
            <select value={assumptions.loan_term_years || 30}
              onChange={(e) => updateAssumption('loan_term_years', Number(e.target.value))}
              className={inputCls}>
              <option value="15">15 yr</option>
              <option value="20">20 yr</option>
              <option value="30">30 yr</option>
            </select>
          </div>

          {/* 2. Down Payment — toggle */}
          <div className="flex flex-col">
            <Toggle mode={modes.down_payment} onToggle={(m) => tog('down_payment', m)} />
            <label className="flex items-center text-xs text-gray-500 mb-0.5 whitespace-nowrap">
              Down Payment<Tip text="% of purchase price paid upfront. Investment loans typically require 20-25%. Default: 20%." />
            </label>
            {modes.down_payment === 'pct' ? (
              <InputWrap type="number" step="1" min="0" max="100"
                value={downPaymentPctInput}
                onChange={(e) => handlePercentChange(setDownPaymentPctInput, 'down_payment_pct', e.target.value, 0.20)}
                className={inputCls} suffix="%"
              />
            ) : (
              <InputWrap type="number" step="1000" min="0"
                value={downPaymentFixedInput}
                onChange={(e) => handleNumberChange(setDownPaymentFixedInput, 'down_payment_fixed', e.target.value, undefined)}
                className={inputCls} placeholder="e.g. 40000" suffix="$"
              />
            )}
          </div>

          {/* 3. MIP — no toggle */}
          <div className="flex flex-col">
            <label className="flex items-center text-xs text-gray-500 mb-0.5 whitespace-nowrap">
              MIP %<Tip text="Mortgage insurance (PMI/MIP). Required if down payment < 20%. Typically 0.5-1.5%/yr. Default: 0%." />
            </label>
            <InputWrap type="number" step="0.1" min="0" max="3"
              value={pmiRateInput}
              onChange={(e) => handlePercentChange(setPmiRateInput, 'pmi_rate', e.target.value, 0.0)}
              className={inputCls} suffix="%"
            />
          </div>

          {/* 4. Interest Rate — no toggle */}
          <div className="flex flex-col">
            <label className="flex items-center text-xs text-gray-500 mb-0.5 whitespace-nowrap">
              Interest Rate (%)<Tip text="Annual mortgage rate. Investment properties currently ~7-8%. Default: 7%." />
            </label>
            <InputWrap type="number" step="0.1" min="0" max="20"
              value={interestRateInput}
              onChange={(e) => handlePercentChange(setInterestRateInput, 'interest_rate', e.target.value, 0.07)}
              className={inputCls} suffix="%"
            />
          </div>

          {/* 5. HOI — toggle */}
          <div className="flex flex-col">
            <Toggle mode={modes.insurance} onToggle={(m) => tog('insurance', m)} />
            <label className="flex items-center text-xs text-gray-500 mb-0.5 whitespace-nowrap">
              HOI<Tip text="Landlord insurance as % of property value/yr. Covers structure and liability. Typically 0.5-1.5%. Default: 1%." />
            </label>
            {modes.insurance === 'pct' ? (
              <InputWrap type="number" step="0.1" min="0" max="5"
                value={insurancePctInput}
                onChange={(e) => handlePercentChange(setInsurancePctInput, 'insurance_pct', e.target.value, 0.01)}
                className={inputCls} suffix="%"
              />
            ) : (
              <InputWrap type="number" step="10" min="0"
                value={insuranceFixedInput}
                onChange={(e) => handleNumberChange(setInsuranceFixedInput, 'insurance_fixed', e.target.value, undefined)}
                className={inputCls} placeholder="0" suffix="$/mo"
              />
            )}
          </div>

          {/* 6. Property Taxes — toggle */}
          <div className="flex flex-col">
            <Toggle mode={modes.property_tax} onToggle={(m) => tog('property_tax', m)} />
            <label className="flex items-center text-xs text-gray-500 mb-0.5 whitespace-nowrap">
              Prop. Taxes<Tip text="Annual property tax as % of property value. National average is ~1.1%, but varies widely by county. Default: 1.1%." />
            </label>
            {modes.property_tax === 'pct' ? (
              <InputWrap type="number" step="0.1" min="0" max="5"
                value={propertyTaxPctInput}
                onChange={(e) => handlePercentChange(setPropertyTaxPctInput, 'property_tax_rate', e.target.value, 0.011)}
                className={inputCls} suffix="%"
              />
            ) : (
              <InputWrap type="number" step="10" min="0"
                value={propertyTaxFixedInput}
                onChange={(e) => handleNumberChange(setPropertyTaxFixedInput, 'property_tax_fixed', e.target.value, undefined)}
                className={inputCls} placeholder="0" suffix="$/mo"
              />
            )}
          </div>

          {/* 7. Maintenance — toggle */}
          <div className="flex flex-col">
            <Toggle mode={modes.maintenance} onToggle={(m) => tog('maintenance', m)} />
            <label className="flex items-center text-xs text-gray-500 mb-0.5 whitespace-nowrap">
              Maint.<Tip text="Annual repairs as % of property value. Budget 1-2% for older homes. Default: 1%." />
            </label>
            {modes.maintenance === 'pct' ? (
              <InputWrap type="number" step="0.1" min="0" max="5"
                value={maintenancePctInput}
                onChange={(e) => handlePercentChange(setMaintenancePctInput, 'maintenance_pct', e.target.value, 0.01)}
                className={inputCls} suffix="%"
              />
            ) : (
              <InputWrap type="number" step="10" min="0"
                value={maintenanceFixedInput}
                onChange={(e) => handleNumberChange(setMaintenanceFixedInput, 'maintenance_fixed', e.target.value, undefined)}
                className={inputCls} placeholder="0" suffix="$/mo"
              />
            )}
          </div>

          {/* 8. Vacancy — toggle */}
          <div className="flex flex-col">
            <Toggle mode={modes.vacancy} onToggle={(m) => tog('vacancy', m)} />
            <label className="flex items-center text-xs text-gray-500 mb-0.5 whitespace-nowrap">
              Vacancy<Tip text="% of rent lost between tenants. Typical long-term rental assumptions are often 5-10%. Default: 8%." />
            </label>
            {modes.vacancy === 'pct' ? (
              <InputWrap type="number" step="1" min="0" max="30"
                value={vacancyRateInput}
                onChange={(e) => handlePercentChange(setVacancyRateInput, 'vacancy_rate', e.target.value, 0.08)}
                className={inputCls} suffix="%"
              />
            ) : (
              <InputWrap type="number" step="10" min="0"
                value={vacancyFixedInput}
                onChange={(e) => handleNumberChange(setVacancyFixedInput, 'vacancy_fixed', e.target.value, undefined)}
                className={inputCls} placeholder="0" suffix="$/mo"
              />
            )}
          </div>

          {/* 9. Management — toggle */}
          <div className="flex flex-col">
            <Toggle mode={modes.property_mgmt} onToggle={(m) => tog('property_mgmt', m)} />
            <label className="flex items-center text-xs text-gray-500 mb-0.5 whitespace-nowrap">
              Mgmt<Tip text="Property manager fee as % of rent. Typically 8-12%. Set to 0% if self-managing. Default: 10%." />
            </label>
            {modes.property_mgmt === 'pct' ? (
              <InputWrap type="number" step="1" min="0" max="20"
                value={propertyMgmtPctInput}
                onChange={(e) => handlePercentChange(setPropertyMgmtPctInput, 'property_mgmt_pct', e.target.value, 0.10)}
                className={inputCls} suffix="%"
              />
            ) : (
              <InputWrap type="number" step="10" min="0"
                value={propertyMgmtFixedInput}
                onChange={(e) => handleNumberChange(setPropertyMgmtFixedInput, 'property_mgmt_fixed', e.target.value, undefined)}
                className={inputCls} placeholder="0" suffix="$/mo"
              />
            )}
          </div>

          {/* 10. Closing Costs — toggle */}
          <div className="flex flex-col w-[5.5rem]">
            <Toggle mode={modes.closing} onToggle={(m) => tog('closing', m)} />
            <label className="flex items-center text-xs text-gray-500 mb-0.5 whitespace-nowrap">
              Closing Costs<Tip text="Non-refundable closing costs: lender fees, title insurance, recording fees. Typically 2-4% of price. Deducted from BRRRR refi cash-out. Default: $3,500." />
            </label>
            {modes.closing === 'pct' ? (
              <InputWrap type="number" step="0.5" min="0" max="15"
                value={closingCostsPctInput}
                onChange={(e) => handlePercentChange(setClosingCostsPctInput, 'closing_costs_pct', e.target.value, 0.03)}
                className={inputCls} suffix="%"
              />
            ) : (
              <InputWrap type="number" step="500" min="0"
                value={closingCostsFixedInput}
                onChange={(e) => handleNumberChange(setClosingCostsFixedInput, 'closing_costs_fixed', e.target.value, undefined)}
                className={inputCls} placeholder="0" suffix="$"
              />
            )}
          </div>

          {/* 10b. Escrow — toggle */}
          <div className="flex flex-col">
            <Toggle mode={modes.escrow} onToggle={(m) => tog('escrow', m)} />
            <label className="flex items-center text-xs text-gray-500 mb-0.5 whitespace-nowrap">
              Escrow<Tip text="Prepaid taxes &amp; insurance deposited into escrow at closing. Refunded when you refinance or sell — roughly nets out. Default: 3%." />
            </label>
            {modes.escrow === 'pct' ? (
              <InputWrap type="number" step="0.5" min="0" max="15"
                value={escrowPctInput}
                onChange={(e) => handlePercentChange(setEscrowPctInput, 'escrow_pct', e.target.value, 0.03)}
                className={inputCls} suffix="%"
              />
            ) : (
              <InputWrap type="number" step="500" min="0"
                value={escrowFixedInput}
                onChange={(e) => handleNumberChange(setEscrowFixedInput, 'escrow_fixed', e.target.value, undefined)}
                className={inputCls} placeholder="0" suffix="$"
              />
            )}
          </div>

          {/* 11. Reserves $ — no toggle */}
          <div className="flex flex-col">
            <label className="flex items-center text-xs text-gray-500 mb-0.5 whitespace-nowrap">
              Reserves $<Tip text="Cash held back at purchase for emergencies. Included in total cash invested for CoC. Default: $5,000." />
            </label>
            <InputWrap type="number" step="500" min="0" max="50000"
              value={reservesInput}
              onChange={(e) => handleNumberChange(setReservesInput, 'reserves_amount', e.target.value, 5000)}
              className={inputCls} suffix="$"
            />
          </div>

          <button onClick={resetAssumptions}
            className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 whitespace-nowrap self-end">
            Reset
          </button>
          <span className="text-xs text-green-600 font-medium self-end pb-0.5">Live</span>
        </div>
      </div>
    </div>
  );
};

export default AssumptionsBar;
